import React, {useEffect, useState} from "react";
import {Box, Button, Dialog, IconButton, InputBase, MenuItem, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallIcon, Cancel01Icon, Clock01Icon, Refresh01Icon} from "@hugeicons/core-free-icons";
import {DateRangePickerMenu} from "../dashboard/components/DateRangePickerMenu";
import {PillSelect} from "../dashboard/components/Panel";

export const EMPTY_FILTERS = {
  id: "", caller: "", vdn: "", status: "", tagIds: [], start: null, end: null, dateLabel: null,
  resolved: "", annotated: "", monitoredBy: "", agent: "", transferred: "", minDuration: "", maxDuration: "", durationUnit: "sec",
};

const DURATION_UNITS = [{value: "sec", label: "Seconds"}, {value: "min", label: "Minutes"}];

const formatSeconds = (total) => {
  const seconds = Math.round(total);
  if (seconds < 60) return `${seconds} s`;
  const rest = seconds % 60;
  return rest ? `${Math.floor(seconds / 60)} min ${rest} s` : `${seconds / 60} min`;
};

const TAGS_COLLAPSED = 5;

const yesNo =(yes, no, all) => [{value: "", label: all}, {value: "true", label: yes}, {value: "false", label: no}];

const inputSx = {
  height: 40, px: 1.5, borderRadius: "12px", bgcolor: "#FAFAFD", fontSize: 14, color: "#23264D",
  "&:focus-within": {bgcolor: "rgba(132, 145, 247, 0.08)"},
};

const durationInputSx = {
  fontSize: 14, color: "#23264D",
  "& input": {textAlign: "center", MozAppearance: "textfield"},
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {WebkitAppearance: "none", margin: 0},
};

const buttonSx = {
  px: 2, py: 1, borderRadius: "12px", textTransform: "none", fontSize: 13, fontWeight: 600,
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

const FilterChip = ({selected, onClick, children}) => (
  <Box component={"button"} type={"button"} onClick={onClick} aria-pressed={selected} sx={{
    px: 1.5, py: 0.75, border: 0, borderRadius: "999px", cursor: "pointer", fontFamily: "inherit",
    fontSize: 13, fontWeight: 500,
    color: selected ? "#4B55C9" : "#6B6B80",
    bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD",
    "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.22)" : "#ECECF3"},
  }}>
    {children}
  </Box>
);

const Segmented = ({value, options, onChange, ariaLabel}) => (
  <Box role={"group"} aria-label={ariaLabel} sx={{display: "flex", gap: 0.5, p: 0.5, borderRadius: "12px", bgcolor: "#FAFAFD"}}>
    {options.map(option => {
      const selected = value === option.value;
      return (
        <Box key={option.value} component={"button"} type={"button"} aria-pressed={selected} onClick={() => onChange(option.value)} sx={{
          flex: 1, height: 32, border: 0, borderRadius: "9px", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
          color: selected ? "#4B55C9" : "#6B6B80",
          bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "transparent",
          transition: "background-color 0.2s ease, color 0.2s ease",
          "&:hover": {bgcolor: selected ? "rgba(132, 145, 247, 0.22)" : "#ECECF3"},
        }}>
          {option.label}
        </Box>
      );
    })}
  </Box>
);

const StepButton = ({onClick, ariaLabel, children}) => (
  <Box component={"button"} type={"button"} onClick={onClick} aria-label={ariaLabel} sx={{
    width: 30, height: 30, flexShrink: 0, border: 0, borderRadius: "9px", cursor: "pointer", fontFamily: "inherit",
    fontSize: 16, lineHeight: 1, color: "#6B6B80", bgcolor: "transparent", transition: "background-color 0.2s ease",
    "&:hover": {bgcolor: "#ECECF3", color: "#4B55C9"},
  }}>
    {children}
  </Box>
);

const NumberField = ({label, value, onChange, step, suffix, ariaLabel}) => {
  const bump = (delta) => onChange(String(Math.max(0, Math.round(((Number(value) || 0) + delta) * 100) / 100)));
  return (
    <Box>
      <Typography sx={{mb: 0.5, ml: 0.5, fontSize: 12, color: "#8A8AA0"}}>{label}</Typography>
      <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{...inputSx, px: 0.5}}>
        <StepButton onClick={() => bump(-step)} ariaLabel={`Decrease ${ariaLabel.toLowerCase()}`}>−</StepButton>
        <InputBase fullWidth type={"number"} placeholder={"—"} value={value} onChange={(e) => onChange(e.target.value)}
                   sx={durationInputSx} inputProps={{min: 0, "aria-label": ariaLabel}}/>
        <Typography sx={{fontSize: 12, fontWeight: 600, color: "#8A8AA0"}}>{suffix}</Typography>
        <StepButton onClick={() => bump(step)} ariaLabel={`Increase ${ariaLabel.toLowerCase()}`}>+</StepButton>
      </Stack>
    </Box>
  );
};

const Field = ({label, icon, children}) => (
  <Box>
    <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{mb: 0.75, color: "#8491F7"}}>
      {icon && <HugeiconsIcon icon={icon} size={12}/>}
      <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "inherit"}}>{label}</Typography>
    </Stack>
    {children}
  </Box>
);

// Filters of the supervision list beyond the search box and pills. value: EMPTY_FILTERS shape; onApply(next) sets all of
// them at once (the list combines them with AND). tagNames: {tagUid: name}; statusOptions: [{value, label}];
// members: project members (supervised by); isVoice: resolved / supervised by / transferred are voice only;
// isAnnotator: annotated is for annotators only.
export const SupervisionFiltersModal = ({
  open, onClose, value, onApply, tagNames, statusOptions, members, isVoice, isAnnotator,
}) => {
  const memberOptions = [
    {value: "", label: "Anyone"},
    ...members.map(member => ({
      value: member.username,
      label: [member.name, member.lastname].filter(Boolean).join(" ") || member.username,
    })),
  ];
  const [draft, setDraft] = useState(value);
  const [showAllTags, setShowAllTags] = useState(false);
  const tagEntries = Object.entries(tagNames);
  const visibleTags = showAllTags ? tagEntries : tagEntries.slice(0, TAGS_COLLAPSED);
  useEffect(() => {
    if (open) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key, next) => setDraft(current => ({...current, [key]: next}));
  const toggleTag = (tagId) => set("tagIds", draft.tagIds.includes(tagId)
    ? draft.tagIds.filter(item => item !== tagId)
    : [...draft.tagIds, tagId]);

  const factor = draft.durationUnit === "min" ? 60 : 1;
  const durationStep = draft.durationUnit === "min" ? 1 : 10;
  const durationSuffix = draft.durationUnit === "min" ? "min" : "sec";
  const minSeconds = draft.minDuration !== "" ? Number(draft.minDuration) * factor : null;
  const maxSeconds = draft.maxDuration !== "" ? Number(draft.maxDuration) * factor : null;
  const durationInvalid = minSeconds !== null && maxSeconds !== null && minSeconds > maxSeconds;
  let durationHint = "Leave empty for no limit";
  if (durationInvalid) durationHint = "Min is greater than max";
  else if (minSeconds !== null && maxSeconds !== null) durationHint = `From ${formatSeconds(minSeconds)} to ${formatSeconds(maxSeconds)}`;
  else if (minSeconds !== null) durationHint = `At least ${formatSeconds(minSeconds)}`;
  else if (maxSeconds !== null) durationHint = `Up to ${formatSeconds(maxSeconds)}`;

  const changeDurationUnit = (unit) => setDraft(current => {
    if (unit === current.durationUnit) return current;
    const ratio = unit === "min" ? 1 / 60 : 60;
    const convert = (text) => text === "" ? "" : String(Math.round(Number(text) * ratio * 100) / 100);
    return {...current, durationUnit: unit, minDuration: convert(current.minDuration), maxDuration: convert(current.maxDuration)};
  });

  const selectedCount =[draft.id.trim(), draft.caller.trim(), draft.vdn.trim(), draft.status, draft.tagIds.length, draft.start,
    draft.resolved, draft.annotated, isVoice && draft.monitoredBy, isVoice && draft.agent, draft.minDuration, draft.maxDuration].filter(Boolean).length;

  const apply = () => {
    onApply({...draft, id: draft.id.trim().replace(/^#/, ""), caller: draft.caller.trim(), vdn: draft.vdn.trim()});
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
            PaperProps={{sx: {maxWidth: 720, borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)"}}}>
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} sx={{p: 3, pb: 2}}>
        <Box>
          <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>Filter conversations</Typography>
          <Typography sx={{mt: 0.25, fontSize: 13, color: "#8A8AA0"}}>
            Only conversations matching all the selected filters are shown.
          </Typography>
        </Box>
        <IconButton size={"small"} onClick={onClose} sx={{color: "#8A8AA0"}} aria-label={"Close"}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>

      <Stack gap={2.5} sx={{px: 3, pb: 2.5}}>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
          <Field label={"# CONVERSATION ID"}>
            <InputBase fullWidth placeholder={"e.g. 1234"} value={draft.id} onChange={(e) => set("id", e.target.value)} sx={inputSx}/>
          </Field>
          <Field label={"DATE"}>
            <DateRangePickerMenu
              value={draft.start && draft.end ? {start: draft.start, end: draft.end, label: draft.dateLabel} : null}
              onChange={(next) => setDraft(current => next
                ? {...current, start: next.start.toDate(), end: next.end.toDate(), dateLabel: next.label}
                : {...current, start: null, end: null, dateLabel: null})}
              ariaLabel={"Filter by date"}
            />
          </Field>
        </Box>

        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
          <Field label={"VDN NUMBER"} icon={CallIcon}>
            <InputBase fullWidth placeholder={"e.g. 7001"} value={draft.vdn} onChange={(e) => set("vdn", e.target.value)} sx={inputSx}/>
          </Field>
          <Field label={"CALLER"} icon={CallIcon}>
            <InputBase fullWidth placeholder={"Name or number"} value={draft.caller} onChange={(e) => set("caller", e.target.value)} sx={inputSx}/>
          </Field>
        </Box>

        <Field label={"STATUS"}>
          <Stack direction={"row"} gap={0.75} flexWrap={"wrap"}>
            {statusOptions.filter(option => option.value).map(option => (
              <FilterChip key={option.value} selected={draft.status === option.value}
                          onClick={() => set("status", draft.status === option.value ? "" : option.value)}>
                {option.label}
              </FilterChip>
            ))}
          </Stack>
        </Field>

        {(isVoice || isAnnotator) && (
          <>
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
              {isVoice && (
                <Field label={"RESOLVED"}>
                  <Segmented value={draft.resolved} options={yesNo("Resolved", "Unresolved", "Any")}
                             onChange={(next) => set("resolved", next)} ariaLabel={"Filter by resolved"}/>
                </Field>
              )}
              {isVoice && (
                <Field label={"SUPERVISED BY"}>
                  <Select
                    fullWidth displayEmpty variant={"standard"} disableUnderline
                    value={draft.monitoredBy}
                    onChange={(e) => set("monitoredBy", e.target.value)}
                    renderValue={(selected) => memberOptions.find(option => option.value === selected)?.label ?? selected}
                    inputProps={{"aria-label": "Filter by supervisor"}}
                    sx={{
                      ...inputSx,
                      fontSize: 13,
                      color: draft.monitoredBy ? "#4B55C9" : "#6B6B80",
                      fontWeight: draft.monitoredBy ? 600 : 400,
                      bgcolor: draft.monitoredBy ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD",
                      transition: "background-color 0.2s ease, color 0.2s ease",
                      "& .MuiSelect-select": {
                        display: "flex", alignItems: "center", height: "100% !important", py: 0, minHeight: "unset",
                        pr: "28px !important", bgcolor: "transparent !important",
                      },
                      "& .MuiSelect-icon": {right: 10, color: draft.monitoredBy ? "#4B55C9" : "#8A8AA0"},
                    }}
                    MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5, maxHeight: 220, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}, MenuListProps: {dense: true, sx: {py: 0.5}}}}
                  >
                    {memberOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{
                        mx: 0.5, my: 0, minHeight: 32, borderRadius: "8px", fontSize: 13, color: "#23264D",
                        "&.Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"},
                        "&.Mui-selected:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"},
                      }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Field>
              )}
              {isVoice && (
                <Field label={"AGENT"}>
                  <Select
                    fullWidth displayEmpty variant={"standard"} disableUnderline
                    value={draft.agent}
                    onChange={(e) => set("agent", e.target.value)}
                    renderValue={(selected) => memberOptions.find(option => option.value === selected)?.label ?? selected}
                    inputProps={{"aria-label": "Filter by agent"}}
                    sx={{
                      ...inputSx,
                      fontSize: 13,
                      color: draft.agent ? "#4B55C9" : "#6B6B80",
                      fontWeight: draft.agent ? 600 : 400,
                      bgcolor: draft.agent ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD",
                      transition: "background-color 0.2s ease, color 0.2s ease",
                      "& .MuiSelect-select": {
                        display: "flex", alignItems: "center", height: "100% !important", py: 0, minHeight: "unset",
                        pr: "28px !important", bgcolor: "transparent !important",
                      },
                      "& .MuiSelect-icon": {right: 10, color: draft.agent ? "#4B55C9" : "#8A8AA0"},
                    }}
                    MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5, maxHeight: 220, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}, MenuListProps: {dense: true, sx: {py: 0.5}}}}
                  >
                    {memberOptions.map(option => (
                      <MenuItem key={option.value} value={option.value} sx={{
                        mx: 0.5, my: 0, minHeight: 32, borderRadius: "8px", fontSize: 13, color: "#23264D",
                        "&.Mui-selected": {bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"},
                        "&.Mui-selected:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"},
                      }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Field>
              )}
              {isAnnotator && (
                <Field label={"ANNOTATED"}>
                  <Segmented value={draft.annotated} options={yesNo("Annotated", "Not annotated", "Any")}
                             onChange={(next) => set("annotated", next)} ariaLabel={"Filter by annotated"}/>
                </Field>
              )}
            </Box>
            {isVoice && (
              <Field label={"DURATION"} icon={Clock01Icon}>
                <Stack direction={{xs: "column", sm: "row"}} gap={2} alignItems={{xs: "stretch", sm: "flex-end"}}>
                  <Box sx={{flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2}}>
                    <NumberField label={"Min"} value={draft.minDuration} onChange={(next) => set("minDuration", next)}
                                 step={durationStep} suffix={durationSuffix} ariaLabel={"Minimum duration"}/>
                    <NumberField label={"Max"} value={draft.maxDuration} onChange={(next) => set("maxDuration", next)}
                                 step={durationStep} suffix={durationSuffix} ariaLabel={"Maximum duration"}/>
                  </Box>
                  <Box sx={{width: {xs: "100%", sm: 200}, flexShrink: 0}}>
                    <Segmented value={draft.durationUnit} options={DURATION_UNITS} onChange={changeDurationUnit} ariaLabel={"Duration unit"}/>
                  </Box>
                </Stack>
                <Typography sx={{mt: 0.75, fontSize: 12, color: durationInvalid ? "#DC2626" : "#8A8AA0"}}>{durationHint}</Typography>
              </Field>
            )}
          </>
        )}

        {/* TODO: enable once the backend supports transferred
        {isVoice && (
          <Stack direction={"row"} gap={3} flexWrap={"wrap"}>
            <Field label={"TRANSFERRED"}>
              <PillSelect value={draft.transferred} options={yesNo("Transferred", "Not transferred", "Any")}
                          onChange={(next) => set("transferred", next)} placeholder={"Any"} ariaLabel={"Filter by transferred"}/>
            </Field>
          </Stack>
        )}
        */}

        {tagEntries.length > 0 && (
          <Field label={"TAGS"}>
            <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} alignItems={"center"}>
              {visibleTags.map(([tagId, name]) => (
                <FilterChip key={tagId} selected={draft.tagIds.includes(tagId)} onClick={() => toggleTag(tagId)}>
                  {name}
                </FilterChip>
              ))}
              {tagEntries.length > TAGS_COLLAPSED && (
                <Box component={"button"} type={"button"} onClick={() => setShowAllTags(current => !current)} sx={{
                  px: 1, py: 0.75, border: 0, bgcolor: "transparent", cursor: "pointer", fontFamily: "inherit",
                  fontSize: 13, fontWeight: 600, color: "#4B55C9", "&:hover": {textDecoration: "underline"},
                }}>
                  {showAllTags ? "Show less" : `Show ${tagEntries.length - TAGS_COLLAPSED} more`}
                </Box>
              )}
            </Stack>
          </Field>
        )}
      </Stack>

      <Stack direction={"row"} justifyContent={"space-between"} sx={{px: 3, pb: 3}}>
        <Button aria-label={"Reset all filters"} onClick={() => setDraft(EMPTY_FILTERS)}
                startIcon={<HugeiconsIcon icon={Refresh01Icon} size={17}/>}
                sx={{...buttonSx, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"}}}>
          Reset
        </Button>
        <Stack direction={"row"} gap={1}>
          <Button sx={buttonSx} onClick={onClose}>Cancel</Button>
          <Button sx={{...buttonSx, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.22)"}}}
                  onClick={apply}>
            Apply{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
